import { c as _c } from "react/compiler-runtime";
import { BetaChip } from "@components/common/beta-chip";
import { FaroTextButton } from "@components/common/faro-text-button";
import { TabActionButtonsWrappedContext, WRAPPED_TAB_ACTION_BUTTONS_SPACING } from "@components/common/tabs/tab-action-buttons-wrapped-context";
import { SendFeedbackDialog } from "@components/header/send-feedback-dialog";
import { FontWeights, Popover, Tab, Tabs } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Box, Grid, Stack } from "@mui-wrapper";
import { getPreservedQueryParams, useAppParams } from "@router/router-helper";
import { SecuredRoute } from "@router/secured-route";
import { TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { CONTENT_MAX_WIDTH, DEFAULT_TAB_UNDERLINE, TAB_WITH_NO_UNDERLINE } from "@styles/common-styles";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { TABS_CONTENT_PADDING } from "@utils/ui-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
/**
 * A general component which generates tabs that are changing the routes
 */
export function TabsWithRoutes({
  selectedTab,
  tabs,
  dataTestId,
  trackingEventPageName,
  selectedProject = null,
  requiredAttribute,
  isLoading = false
}) {
  const {
    trackEvent
  } = useTrackEvent();
  const {
    memberId
  } = useAppParams();
  const {
    hasUserPermissionCompanyLevel
  } = useHasUserValidRoleCompanyLevel();
  const {
    hasUserPermissionProjectLevel
  } = useHasUserValidPermissionProjectLevel();
  const popoverRefs = useRef({});
  const [activePopoverIndex, setActivePopoverIndex] = useState(null);
  const [shouldShowGiveFeedback, setShouldShowGiveFeedback] = useState(false);
  const [areActionButtonsWrapped, setAreActionButtonsWrapped] = useState(false);

  // Get the current location and preserved query parameters
  const location = useLocation();
  const preservedSearch = getPreservedQueryParams(location.search);

  // Returns the content of the selected tab
  const selectedTabContent = useMemo(() => {
    const tabToShow = tabs.find(({
      route
    }) => route === selectedTab);

    /**
     * Checks whether the required attribute is defined and not null.
     */
    function isRequiredAttributeDefined(requiredAttribute_0) {
      return requiredAttribute_0 !== undefined && requiredAttribute_0 !== null;
    }

    /** Show the loading content while the required attribute is not defined yet or if the data is loading */
    if (!isRequiredAttributeDefined(requiredAttribute) || isLoading) {
      return tabToShow?.loadingContent;
    }

    /**
     * If the tab is still loading its own data (isTabLoading), show loading content.
     * This prevents redirecting to "Access denied" when the tab is accessed via deeplink
     * while its data is still being fetched.
     */
    if (tabToShow?.isTabLoading) {
      return tabToShow?.loadingContent;
    }
    return <SecuredRoute requiredRoleCompanyLevel={tabToShow?.requiredRoleCompanyLevel} requiredFeatureCompanyLevel={tabToShow?.requiredFeatureCompanyLevel} requiredPermissionProjectLevel={tabToShow?.requiredPermissionProjectLevel} isHidden={tabToShow?.isHidden} loadingContent={tabToShow?.loadingContent}>
        {tabToShow?.content(requiredAttribute)}
      </SecuredRoute>;
  }, [tabs, requiredAttribute, isLoading, selectedTab]);

  // Returns the action buttons of the selected tab
  const selectedTabActionButtons = useMemo(() => {
    return tabs.find(({
      route: route_0
    }) => route_0 === selectedTab)?.actionButtons;
  }, [tabs, selectedTab]);

  /**
   * Returns whether a tab is hidden based on the disabled property
   * or on the user's permissions if provided.
   *
   * @returns True if the tab should be hidden.
   */
  const isTabHidden = useCallback(tab => {
    // If the tab is still loading, hide it from the tab list.
    // This is checked before the selectedTab check because we want to hide
    // loading tabs even if they are selected (accessed via deeplink).
    // The SecuredRoute will show loading content instead of redirecting.
    if (tab.isTabLoading) {
      return true;
    }

    // Never hide the selected tab because it was probably open from a deep link.
    // Otherwise there is an error thrown in the console that the route does not exist.
    // If user does not have access to the tab it will be redirected to the forbidden page
    // by the secured route component.
    if (selectedTab === tab.route) {
      return false;
    }
    if (tab.isHidden) {
      return true;
    }
    if (tab.requiredRoleCompanyLevel && !hasUserPermissionCompanyLevel({
      roleName: tab.requiredRoleCompanyLevel,
      memberId
    })) {
      return true;
    }
    if (tab.requiredPermissionProjectLevel && !hasUserPermissionProjectLevel({
      permissionName: tab.requiredPermissionProjectLevel,
      project: selectedProject
    })) {
      return true;
    }
    return false;
  }, [selectedTab, hasUserPermissionCompanyLevel, hasUserPermissionProjectLevel, memberId, selectedProject]);

  // Filter out hidden tabs
  const visibleTabs = useMemo(() => {
    return tabs.filter(tab_0 => tab_0 && !isTabHidden(tab_0));
  }, [tabs, isTabHidden]);
  return <TabActionButtonsWrappedContext value={areActionButtonsWrapped}>
      <Box component="div" sx={{
      width: "100%",
      position: "relative"
    }} data-testid={dataTestId}>
        <Box component="div" sx={{
        width: "100%",
        // Cap the tab bar (tabs + action buttons) to the content width and center it so it
        // keeps the same size and position across tabs and stays aligned with the content.
        maxWidth: CONTENT_MAX_WIDTH,
        mx: "auto",
        backgroundColor: "background.default",
        paddingTop: 2,
        // Every tab's content starts the same distance below the tabs, whatever the top margin
        // of its first element, so the content does not jump when switching pages, tabs or
        // views. `&&` outranks that element's own margin.
        "&& + *": {
          mt: TABS_CONTENT_PADDING
        }
      }}>
          <GetTabTitlesWithActions selectedTabActionButtons={selectedTabActionButtons} isWrapped={areActionButtonsWrapped} onIsWrappedChange={setAreActionButtonsWrapped}>
            <Tabs value={selectedTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{
            boxShadow: DEFAULT_TAB_UNDERLINE,
            flexGrow: 1
          }}>
              {visibleTabs.map((tab_1, index) => <Tab ref={el => {
              popoverRefs.current[index] = el;
            }} onMouseEnter={() => {
              if (tab_1.popover) {
                setActivePopoverIndex(index);
              }
            }} onMouseLeave={() => {
              if (tab_1.popover) {
                setActivePopoverIndex(null);
              }
            }} draggable={false} key={tab_1.route} component={Link} label={<Stack direction="row" spacing={1}>
                      {tab_1.label}
                      {tab_1.isBetaFeature && <BetaChip sx={{
                bottom: "10px"
              }} />}
                      {tab_1.isNewFeature && <BetaChip sx={{
                bottom: "10px"
              }} text="NEW" />}
                      {tab_1.popover && <Popover dark open={activePopoverIndex === index} anchorEl={popoverRefs.current[index]} onClose={() => setActivePopoverIndex(null)} closeOnClickOutside={false} title={tab_1.popover.title} description={<>
                              {tab_1.popover.description}
                              {tab_1.popover.shouldShowFeedbackButton && <Stack sx={{
                  alignItems: "flex-end"
                }}>
                                  <FaroTextButton onClick={() => {
                    setShouldShowGiveFeedback(true);
                    setActivePopoverIndex(null);
                  }} sx={{
                    fontWeight: FontWeights.Bold,
                    "&:focus": {
                      backgroundColor: "transparent"
                    }
                  }}>
                                    Give feedback
                                  </FaroTextButton>
                                </Stack>}
                            </>} />}
                    </Stack>} value={tab_1.route} relative="path" to={{
              pathname: `../${tab_1.route}`,
              search: preservedSearch
            }} onClick={() => {
              trackEvent({
                name: trackingEventPageName,
                props: {
                  tab: getTrackingTabName(tab_1.label, tab_1.trackingTabName)
                }
              });
            }} />)}
            </Tabs>
          </GetTabTitlesWithActions>
        </Box>
        {selectedTabContent}

        {/** This dialog is used for submitting feedback about teams (user groups) */}
        <SendFeedbackDialog isDialogOpen={shouldShowGiveFeedback} onClose={() => {
        setShouldShowGiveFeedback(false);
        setActivePopoverIndex(null);
      }} title="How can we improve teams?" featureName={`${TEAMS_DISPLAY_NAME} (User Groups)`} confirmText="Submit" />
      </Box>
    </TabActionButtonsWrappedContext>;
}
const HEIGHT_OF_COMPONENT_WITHOUT_BREAKS = 52;

/**
 * Add action buttons at the right of a specific tab
 */
function GetTabTitlesWithActions(t0) {
  const $ = _c(12);
  const {
    selectedTabActionButtons,
    children,
    isWrapped,
    onIsWrappedChange
  } = t0;
  const containerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onIsWrappedChange) {
    t1 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const observer = new ResizeObserver(entries => {
        const isBreakingLines = entries[0].target.clientHeight > HEIGHT_OF_COMPONENT_WITHOUT_BREAKS;
        onIsWrappedChange(isBreakingLines);
      });
      observer.observe(container);
      return () => {
        observer.unobserve(container);
      };
    };
    t2 = [onIsWrappedChange];
    $[0] = onIsWrappedChange;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = isWrapped ? WRAPPED_TAB_ACTION_BUTTONS_SPACING : 0;
  const t4 = isWrapped ? TAB_WITH_NO_UNDERLINE : DEFAULT_TAB_UNDERLINE;
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "end",
      flexGrow: 1,
      mt: t3,
      boxShadow: t4
    };
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== selectedTabActionButtons || $[7] !== t5) {
    t6 = <Grid sx={t5}>{selectedTabActionButtons}</Grid>;
    $[6] = selectedTabActionButtons;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== children || $[10] !== t6) {
    t7 = <Grid container={true} ref={containerRef}>{children}{t6}</Grid>;
    $[9] = children;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

/** Returns the name of a tab for event-tracking based on its trackingTabName or if not provided the label */
function getTrackingTabName(label, trackingTabName) {
  if (trackingTabName) {
    return trackingTabName;
  }
  return typeof label === "string" ? label : "";
}